"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import type { Dictionary } from "@/dictionaries/types";
import type { Locale } from "@/i18n-config";
import { SPLASH_TOTAL_MS, hasLaunchCompleted, markLaunchCompleted, markOnboardingSeen, readOnboardingSeen, shouldShowIntro } from "@/lib/app-launch";
import useIsAppShell from "@/hooks/useIsAppShell";
import AppSplashScreen from "./AppSplashScreen";

/**
 * The launch sequence, assembled — splash, then (once, ever) the intro.
 *
 * DOM ORDER IS THE STACKING ORDER AND IT IS DELIBERATE: the intro renders
 * FIRST and the splash SECOND, so the splash sits above an intro that is
 * already composed and laid out. The handoff is then the splash simply fading
 * out; there is no cut, no mount flash, and no frame where the user sees the
 * real page between the two. Both carry `z-progress`, so ordinary source order
 * decides — which is why neither needs a z-index of its own.
 *
 * THE SPLASH IS NOT CONDITIONAL HERE. It is always rendered, always
 * `display: none` outside an installed shell, and always dismissed by CSS —
 * see `src/lib/app-launch.ts` for why none of that may become React state. The
 * only thing this component does for the splash is notice when its lifetime has
 * elapsed, and record that on `<html>` so a later remount cannot replay it.
 *
 * THE INTRO IS `dynamic()` AND THE SPLASH IS NOT. The splash must be in the
 * server-rendered HTML or it cannot paint before hydration; the intro must NOT
 * be, because the overwhelming majority of documents are browser tabs that will
 * never show it, and framer-motion plus four illustrations is real weight to
 * put in the bundle every visitor downloads for a surface almost none of them
 * will see. `ssr: false` is the honest declaration: nothing about it can be
 * decided on the server, since `localStorage` is a client fact.
 */
const AppIntroCarousel = dynamic(() => import("./AppIntroCarousel"), {
  ssr: false
});
type LaunchExperienceProps = {
  lang: Locale;
  copy: Dictionary["onboarding"];
};
export default function LaunchExperience(t0) {
  const $ = _c(14);
  const {
    lang,
    copy
  } = t0;
  const isAppShell = useIsAppShell();
  const [introOpen, setIntroOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp, t1);
  let t2;
  let t3;
  if ($[1] !== isAppShell) {
    t2 = () => {
      if (!shouldShowIntro(isAppShell, readOnboardingSeen())) {
        return;
      }
      setIntroOpen(true);
    };
    t3 = [isAppShell];
    $[1] = isAppShell;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      markOnboardingSeen();
      setIntroOpen(false);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const dismissIntro = t4;
  let t5;
  if ($[5] !== copy || $[6] !== introOpen || $[7] !== lang) {
    t5 = introOpen ? <AppIntroCarousel lang={lang} copy={copy} onDone={dismissIntro} /> : null;
    $[5] = copy;
    $[6] = introOpen;
    $[7] = lang;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== copy.tagline) {
    t6 = <AppSplashScreen tagline={copy.tagline} />;
    $[9] = copy.tagline;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function _temp() {
  if (hasLaunchCompleted()) {
    return;
  }
  const timer = window.setTimeout(markLaunchCompleted, SPLASH_TOTAL_MS);
  return () => window.clearTimeout(timer);
}